import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';

export interface LeadGenForm {
  id: number;
  name: string;
  publicKey: string;
  description?: string;
  fieldsJson?: string;
  formType?: string;
  published: boolean;
  active: boolean;
}

export interface LeadGenGradeConfig {
  id: number;
  gradeCode: string;
  minScore: number;
  maxScore?: number;
  sortOrder: number;
  active: boolean;
}

export interface LeadGenCampaign {
  id: number;
  name: string;
  description?: string;
  budget: number;
  cost: number;
  channel?: string;
  targetAudience?: string;
  goals?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  revenueAttribution: number;
  active: boolean;
}

export interface LeadGenLandingPage {
  id: number;
  title: string;
  slug: string;
  contentHtml?: string;
  templateId?: string;
  customDomain?: string;
  abTestVariant?: string;
  version: number;
  published: boolean;
  viewsCount: number;
  conversionsCount: number;
}

export interface LeadGenScoringRule {
  id: number;
  ruleName: string;
  eventType: string;
  scoreDelta: number;
  conditionJson?: string;
  ruleCategory?: string;
  decayDays?: number;
  active: boolean;
}

export interface LeadGenRoutingRule {
  id: number;
  ruleName: string;
  priorityOrder: number;
  conditionJson: string;
  assignEmployeeId?: number;
  assignTeamCode?: string;
  territoryId?: number;
  active: boolean;
}

export interface LeadGenAbExperiment {
  id: number;
  experimentName: string;
  slug: string;
  variantPageIds: string;
  trafficWeights: string;
  statusCode: string;
  winnerPageId?: number;
}

export interface LeadGenEnrichment {
  id: number;
  leadId: number;
  companyName?: string;
  industry?: string;
  website?: string;
  employeeCount?: number;
  revenue?: number;
  country?: string;
  state?: string;
  city?: string;
  enrichmentSource?: string;
}

export interface LeadGenVisitorProfile {
  id: number;
  visitorCookieId: string;
  ipAddress?: string;
  email?: string;
  phone?: string;
  name?: string;
  leadId?: number;
}

export interface LeadGenConsentRecord {
  id: number;
  email: string;
  consentVersion: string;
  cookieConsent: boolean;
  privacyAcceptance: boolean;
  createdAt?: string;
}

export interface LeadGenSource {
  id: number;
  code: string;
  name: string;
  sourceTypeCode?: string;
  integrationConfigJson?: string;
  webhookConfigured?: boolean;
  active: boolean;
}

export interface LeadGenSourceRequest extends Partial<LeadGenSource> {
  webhookSecret?: string;
}

export interface LeadGenSourceType {
  code: string;
  name: string;
  channelGroup: string;
}

export interface LeadDuplicateCandidate {
  id: number;
  incomingSubmissionId?: number;
  incomingLeadId?: number;
  matchedLeadId: number;
  ruleId?: number;
  matchFieldsJson?: string;
  actionCode: string;
  reviewStatus: string;
}

export interface LeadGenDashboardResponse {
  totalVisitors: number;
  totalLeads: number;
  conversions: number;
  conversionRate: number;
  avgLeadScore: number;
  formSubmissions: number;
  activeCampaigns: number;
  activeForms: number;
  publishedLandingPages: number;
  funnel: { stage: string; count: number; percent: string }[];
  topSources: { source: string; leads: number; conversionRate: string; roi: string }[];
  scoreDistribution: { grade: string; count: number }[];
  campaignPerformance: { id: number; name: string; budget: number; cost: number; revenueAttribution: number; roiPercentage: number }[];
}

export interface LeadGenReportsSummary {
  totalLeads: number;
  convertedLeads: number;
  conversionRate: number;
  averageLeadScore: number;
  formSubmissions: number;
  activeCampaigns: number;
  totalCampaignCost: number;
  totalRevenueAttribution: number;
  overallRoiPercent: number;
}

export interface LeadGenLeadSourceReportRow {
  source: string;
  leads: number;
  converted: number;
  conversionRate: number;
  sharePercent: number;
}

export interface LeadGenCampaignReportRow {
  campaignId: number;
  name: string;
  channel: string;
  budget: number;
  cost: number;
  utmCampaign: string;
  active: boolean;
}

export interface LeadGenConversionReportRow {
  eventType: string;
  count: number;
}

export interface LeadGenScoringReportRow {
  grade: string;
  count: number;
  sharePercent: number;
}

export interface LeadGenAttributionReportRow {
  utmSource: string;
  leads: number;
  model: string;
}

export interface LeadGenRoiReportRow {
  campaignId: number;
  campaignName: string;
  attributedLeads: number;
  campaignCost: number;
  leadCost: number;
  revenueAttribution: number;
  roiPercent: number;
}

export interface LeadGenReportsBundle {
  summary: LeadGenReportsSummary;
  leadSourceReport: LeadGenLeadSourceReportRow[];
  campaignReport: LeadGenCampaignReportRow[];
  conversionReport: LeadGenConversionReportRow[];
  scoringReport: LeadGenScoringReportRow[];
  attributionReport: LeadGenAttributionReportRow[];
  roiReport: LeadGenRoiReportRow[];
}

function asReportNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeLeadGenReportsBundle(raw: Record<string, unknown>): LeadGenReportsBundle {
  const leadSourceReport: LeadGenLeadSourceReportRow[] = (
    (raw.leadSourceReport as Record<string, unknown>[] | undefined) ?? []
  ).map((row) => {
    const leads = asReportNumber(row.leads);
    const converted = asReportNumber(row.converted);
    return {
      source: String(row.source ?? 'UNKNOWN'),
      leads,
      converted,
      conversionRate: asReportNumber(row.conversionRate, leads > 0 ? (converted * 100) / leads : 0),
      sharePercent: asReportNumber(row.sharePercent),
    };
  });

  const campaignReport: LeadGenCampaignReportRow[] = (
    (raw.campaignReport as Record<string, unknown>[] | undefined) ?? []
  ).map((row) => ({
    campaignId: asReportNumber(row.campaignId),
    name: String(row.name ?? '—'),
    channel: String(row.channel ?? ''),
    budget: asReportNumber(row.budget),
    cost: asReportNumber(row.cost),
    utmCampaign: String(row.utmCampaign ?? ''),
    active: Boolean(row.active),
  }));

  const conversionReport: LeadGenConversionReportRow[] = (
    (raw.conversionReport as Record<string, unknown>[] | undefined) ?? []
  ).map((row) => ({
    eventType: String(row.eventType ?? 'UNKNOWN'),
    count: asReportNumber(row.count),
  }));

  const totalLeadsFromSources = leadSourceReport.reduce((sum, row) => sum + row.leads, 0);
  const totalLeads = asReportNumber(
    (raw.summary as LeadGenReportsSummary | undefined)?.totalLeads,
    totalLeadsFromSources,
  );

  const scoringRaw = (raw.scoringReport as Record<string, unknown>[] | undefined) ?? [];
  const legacyAvgRow = scoringRaw.find((row) => row.metric === 'averageScore');
  const scoringReport: LeadGenScoringReportRow[] = scoringRaw
    .filter((row) => row.grade != null && row.metric == null)
    .map((row) => {
      const count = asReportNumber(row.count);
      return {
        grade: String(row.grade),
        count,
        sharePercent: asReportNumber(
          row.sharePercent,
          totalLeads > 0 ? Math.round((count * 1000) / totalLeads) / 10 : 0,
        ),
      };
    });

  const attributionReport: LeadGenAttributionReportRow[] = (
    (raw.attributionReport as Record<string, unknown>[] | undefined) ?? []
  ).map((row) => ({
    utmSource: String(row.utmSource ?? 'UNKNOWN'),
    leads: asReportNumber(row.leads),
    model: String(row.model ?? 'MULTI_TOUCH'),
  }));

  const roiReport: LeadGenRoiReportRow[] = (
    (raw.roiReport as Record<string, unknown>[] | undefined) ?? []
  ).map((row) => ({
    campaignId: asReportNumber(row.campaignId),
    campaignName: String(row.campaignName ?? row.name ?? '—'),
    attributedLeads: asReportNumber(row.attributedLeads),
    campaignCost: asReportNumber(row.campaignCost ?? row.cost),
    leadCost: asReportNumber(row.leadCost),
    revenueAttribution: asReportNumber(row.revenueAttribution),
    roiPercent: asReportNumber(row.roiPercent ?? row.roiPercentage),
  }));

  const rawSummary = raw.summary as LeadGenReportsSummary | undefined;
  const convertedLeads = asReportNumber(rawSummary?.convertedLeads);
  const totalCampaignCost = asReportNumber(
    rawSummary?.totalCampaignCost,
    campaignReport.reduce((sum, row) => sum + row.cost, 0),
  );
  const totalRevenueAttribution = asReportNumber(
    rawSummary?.totalRevenueAttribution,
    roiReport.reduce((sum, row) => sum + row.revenueAttribution, 0),
  );
  const overallRoiPercent = asReportNumber(
    rawSummary?.overallRoiPercent,
    totalCampaignCost > 0
      ? Math.round(((totalRevenueAttribution - totalCampaignCost) * 1000) / totalCampaignCost) / 10
      : 0,
  );

  const summary: LeadGenReportsSummary = {
    totalLeads,
    convertedLeads,
    conversionRate: asReportNumber(
      rawSummary?.conversionRate,
      totalLeads > 0 ? Math.round((convertedLeads * 1000) / totalLeads) / 10 : 0,
    ),
    averageLeadScore: asReportNumber(rawSummary?.averageLeadScore, asReportNumber(legacyAvgRow?.value)),
    formSubmissions: asReportNumber(
      rawSummary?.formSubmissions,
      conversionReport.find((row) => row.eventType === 'FORM_SUBMISSION')?.count ?? 0,
    ),
    activeCampaigns: asReportNumber(
      rawSummary?.activeCampaigns,
      campaignReport.filter((row) => row.active).length,
    ),
    totalCampaignCost,
    totalRevenueAttribution,
    overallRoiPercent,
  };

  return {
    summary,
    leadSourceReport,
    campaignReport,
    conversionReport,
    scoringReport,
    attributionReport,
    roiReport,
  };
}

export const leadgenApi = {
  listForms: () =>
    unwrap(api.get<ApiResponse<LeadGenForm[]>>('/v1/lead-gen/forms')),

  createForm: (body: Partial<LeadGenForm>) =>
    unwrap(api.post<ApiResponse<LeadGenForm>>('/v1/lead-gen/forms', body)),

  updateForm: (id: number, body: Partial<LeadGenForm>) =>
    unwrap(api.put<ApiResponse<LeadGenForm>>(`/v1/lead-gen/forms/${id}`, body)),

  deleteForm: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/forms/${id}`)),

  getPublicForm: (publicKey: string) =>
    unwrap(api.get<ApiResponse<LeadGenForm>>(`/v1/lead-gen/public/forms/${publicKey}`)),

  trackVisitor: (body: { formPublicKey: string; cookieId: string; email?: string; phone?: string; name?: string }) =>
    unwrap(api.post<ApiResponse<LeadGenVisitorProfile>>('/v1/lead-gen/public/visitor/track', body)),

  submitPublic: (body: {
    formPublicKey: string;
    fields?: Record<string, unknown>;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    consentGiven: boolean;
    visitorProfileId?: number;
    visitorCookieId?: string;
  }) =>
    unwrap(api.post<ApiResponse<{ submissionId: number }>>('/v1/lead-gen/submissions', body)),

  listCampaigns: () => unwrap(api.get<ApiResponse<LeadGenCampaign[]>>('/v1/lead-gen/campaigns')),
  createCampaign: (body: Partial<LeadGenCampaign>) => unwrap(api.post<ApiResponse<LeadGenCampaign>>('/v1/lead-gen/campaigns', body)),
  updateCampaign: (id: number, body: Partial<LeadGenCampaign>) => unwrap(api.put<ApiResponse<LeadGenCampaign>>(`/v1/lead-gen/campaigns/${id}`, body)),
  deleteCampaign: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/campaigns/${id}`)),
  getCampaignRoi: (id: number) => unwrap(api.get<ApiResponse<Record<string, unknown>>>(`/v1/lead-gen/campaigns/${id}/roi`)),

  listLandingPages: () => unwrap(api.get<ApiResponse<LeadGenLandingPage[]>>('/v1/lead-gen/landing-pages')),
  createLandingPage: (body: Partial<LeadGenLandingPage>) => unwrap(api.post<ApiResponse<LeadGenLandingPage>>('/v1/lead-gen/landing-pages', body)),
  updateLandingPage: (id: number, body: Partial<LeadGenLandingPage>) => unwrap(api.put<ApiResponse<LeadGenLandingPage>>(`/v1/lead-gen/landing-pages/${id}`, body)),
  deleteLandingPage: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/landing-pages/${id}`)),

  listScoringRules: () => unwrap(api.get<ApiResponse<LeadGenScoringRule[]>>('/v1/lead-gen/scoring-rules')),
  createScoringRule: (body: Partial<LeadGenScoringRule>) => unwrap(api.post<ApiResponse<LeadGenScoringRule>>('/v1/lead-gen/scoring-rules', body)),
  updateScoringRule: (id: number, body: Partial<LeadGenScoringRule>) => unwrap(api.put<ApiResponse<LeadGenScoringRule>>(`/v1/lead-gen/scoring-rules/${id}`, body)),
  deleteScoringRule: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/scoring-rules/${id}`)),

  listRoutingRules: () => unwrap(api.get<ApiResponse<LeadGenRoutingRule[]>>('/v1/lead-gen/routing-rules')),
  createRoutingRule: (body: Partial<LeadGenRoutingRule>) => unwrap(api.post<ApiResponse<LeadGenRoutingRule>>('/v1/lead-gen/routing-rules', body)),
  updateRoutingRule: (id: number, body: Partial<LeadGenRoutingRule>) => unwrap(api.put<ApiResponse<LeadGenRoutingRule>>(`/v1/lead-gen/routing-rules/${id}`, body)),
  deleteRoutingRule: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/lead-gen/routing-rules/${id}`)),

  listVisitorProfiles: () => unwrap(api.get<ApiResponse<LeadGenVisitorProfile[]>>('/v1/lead-gen/visitor/profiles')),
  listConsentRecords: () => unwrap(api.get<ApiResponse<LeadGenConsentRecord[]>>('/v1/lead-gen/consent')),
  saveConsent: (body: Partial<LeadGenConsentRecord>) => unwrap(api.post<ApiResponse<LeadGenConsentRecord>>('/v1/lead-gen/consent', body)),

  getDashboard: (period: import('@/types/dashboardPresentation').DashboardPeriod = 'MONTH') =>
    unwrap(api.get<ApiResponse<LeadGenDashboardResponse>>('/v1/lead-gen/dashboard/summary', { params: { period } })),
  getReports: async () =>
    normalizeLeadGenReportsBundle(
      (await unwrap(api.get<ApiResponse<Record<string, unknown>>>('/v1/lead-gen/reports'))) as Record<
        string,
        unknown
      >,
    ),

  listSources: () => unwrap(api.get<ApiResponse<LeadGenSource[]>>('/v1/lead-gen/sources')),
  createSource: (body: LeadGenSourceRequest) => unwrap(api.post<ApiResponse<LeadGenSource>>('/v1/lead-gen/sources', body)),
  listSourceTypes: () => unwrap(api.get<ApiResponse<LeadGenSourceType[]>>('/v1/lead-gen/sources/types')),

  listDuplicateCandidates: () =>
    unwrap(api.get<ApiResponse<LeadDuplicateCandidate[]>>('/v1/lead-gen/duplicates')),
  resolveDuplicateCandidate: (id: number, body: { actionCode: string; sourceLeadId?: number; targetLeadId?: number; reason?: string }) =>
    unwrap(api.post<ApiResponse<LeadDuplicateCandidate>>(`/v1/lead-gen/duplicates/${id}/resolve`, body)),

  getFormEmbed: (id: number) => unwrap(api.get<ApiResponse<{ publicKey: string; embedCodes: Record<string, string> }>>(`/v1/lead-gen/forms/${id}/embed`)),

  getGradeConfig: () => unwrap(api.get<ApiResponse<LeadGenGradeConfig[]>>('/v1/lead-gen/grade-config')),

  savePublicConsent: (body: {
    formPublicKey: string;
    email: string;
    consentVersion: string;
    cookieConsent: boolean;
    privacyAcceptance: boolean;
  }) => unwrap(api.post<ApiResponse<LeadGenConsentRecord>>('/v1/lead-gen/public/consent', body)),

  listAbExperiments: () => unwrap(api.get<ApiResponse<LeadGenAbExperiment[]>>('/v1/lead-gen/ab-experiments')),

  createAbExperiment: (body: Partial<LeadGenAbExperiment>) =>
    unwrap(api.post<ApiResponse<LeadGenAbExperiment>>('/v1/lead-gen/ab-experiments', body)),

  declareAbWinner: (id: number, winnerPageId: number) =>
    unwrap(
      api.post<ApiResponse<LeadGenAbExperiment>>(`/v1/lead-gen/ab-experiments/${id}/winner`, null, {
        params: { winnerPageId },
      }),
    ),

  getEnrichment: (leadId: number) =>
    unwrap(api.get<ApiResponse<LeadGenEnrichment>>(`/v1/lead-gen/enrichment/lead/${leadId}`)),

  enrichFromEmail: (leadId: number, email: string) =>
    unwrap(
      api.post<ApiResponse<LeadGenEnrichment>>(`/v1/lead-gen/enrichment/lead/${leadId}/from-email`, null, {
        params: { email },
      }),
    ),

  upsertEnrichment: (body: Partial<LeadGenEnrichment> & { leadId: number }) =>
    unwrap(api.post<ApiResponse<LeadGenEnrichment>>('/v1/lead-gen/enrichment', body)),
};
